// src/components/RoleForm.js
import React, { useState } from 'react';
import { api } from '../utils/api';
import { PERMISSION_GROUPS } from '../utils/permissions';
import { EditIcon, PlusIcon, AlertTriangleIcon } from './Icon';

export default function RoleForm({ role, onClose, onSaved }) {
  const isEdit = !!role;
  const isAdminRole = !!role && role.name === 'admin';

  const [form, setForm] = useState({
    id:    role?.id || '',
    name:  role?.name || '',
    label: role?.label || '',
  });
  const [perms, setPerms] = useState(() => new Set(role?.permissions || []));
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const togglePerm = (key) => setPerms(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const toggleGroup = (group) => setPerms(prev => {
    const n = new Set(prev);
    const keys = group.perms.map(p => p.key);
    const allOn = keys.every(k => n.has(k));
    keys.forEach(k => (allOn ? n.delete(k) : n.add(k)));
    return n;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    const name = form.name.trim().toLowerCase();
    if (!/^[a-z0-9_]{2,40}$/.test(name)) {
      setErrMsg('Role name must be 2–40 chars: lowercase letters, digits or underscore');
      return;
    }

    setSaving(true);
    const payload = {
      id:          form.id || undefined,
      name,
      label:       form.label.trim(),
      permissions: Array.from(perms),
    };

    try {
      const res = await api.roleSave(payload);
      if (res?.success) onSaved();
      else setErrMsg(res?.error || 'Save failed');
    } catch (err) {
      setErrMsg('Network error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? <EditIcon size={18} /> : <PlusIcon size={18} />}
            <span>{isEdit ? `Edit Role — ${role.label || role.name}` : 'Add New Role'}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-row2">
            <Field label="Role Name *" hint={isAdminRole ? 'The admin role cannot be renamed.' : 'Lowercase id, e.g. sales_lead'}>
              <input
                className="modal-input"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                disabled={isAdminRole}
                placeholder="e.g. sales_lead"
                autoComplete="off"
              />
            </Field>
            <Field label="Display Label">
              <input className="modal-input" value={form.label} onChange={e => update('label', e.target.value)} placeholder="e.g. Sales Lead" />
            </Field>
          </div>

          {isAdminRole && (
            <div className="modal-hint" style={{ marginTop: -4 }}>
              Admin always has full access — its permissions are fixed.
            </div>
          )}

          <div className="role-perm-groups" aria-disabled={isAdminRole}>
            {PERMISSION_GROUPS.map(group => {
              const keys = group.perms.map(p => p.key);
              const allOn = keys.every(k => perms.has(k));
              return (
                <fieldset key={group.module} className="role-perm-group" disabled={isAdminRole}>
                  <legend className="role-perm-legend">
                    <button type="button" className="role-perm-grouptoggle" onClick={() => toggleGroup(group)}>
                      {allOn ? 'Clear' : 'All'}
                    </button>
                    {group.module}
                  </legend>
                  <div className="role-perm-list">
                    {group.perms.map(p => (
                      <label key={p.key} className="role-perm-item">
                        <input
                          type="checkbox"
                          checked={isAdminRole || perms.has(p.key)}
                          onChange={() => togglePerm(p.key)}
                          disabled={isAdminRole}
                        />
                        <span>{p.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              );
            })}
          </div>

          {errMsg && (
            <div className="modal-err">
              <AlertTriangleIcon size={15} /> {errMsg}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving || isAdminRole}>
              {saving ? 'Saving...' : (isEdit ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="modal-field">
      <label className="modal-label">{label}</label>
      {children}
      {hint && <div className="modal-hint">{hint}</div>}
    </div>
  );
}
